import './BadgerPage.scss';
import CircularProgress from '../../../shared/components/CircularProgress/CircularProgress';
import Paper from '../../../shared/components/Paper/Paper';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Alert from '../../../shared/components/Alert/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import Box from '../../../shared/components/Box/Box';
import Input from '../../../shared/components/Input/Input';
import Typography from '../../../shared/components/Typography/Typography';
import { copyMyTimeDay, createTimeEntry, deleteTimeEntry, getMyTimeEntries, getMyWeekSummary, getProjectTimeEntries, getTimeActivities, updateTimeEntry } from '../badger.api';
import { getAllProjects, getOneProject } from '../../projects/project.api';
import { getProjectLdaLines } from '../../lda/lda.api';
import { getInterventions } from '../../interventions/services/intervention.api';
import { getTimeTrackableTasks } from '../../organization/tasks/tasks.api';

const FALLBACK_ACTIVITIES = [
  { code: 'production', label: 'Production projet', category: 'Production', contextType: 'project', requiresProject: true, billable: true },
  { code: 'project_management', label: 'Gestion de projet', category: 'Production', contextType: 'project', requiresProject: true, billable: true },
  { code: 'commercial', label: 'Commercial / relation client', category: 'Commercial', contextType: 'commercial', billable: false },
  { code: 'marketing', label: 'Marketing / prospection', category: 'Commercial', contextType: 'marketing', billable: false },
  { code: 'administrative', label: 'Administratif', category: 'Interne', contextType: 'administrative', billable: false },
  { code: 'training', label: 'Formation', category: 'Interne', contextType: 'training', billable: false },
  { code: 'watch', label: 'Veille technologique / scientifique', category: 'Interne', contextType: 'watch', billable: false },
  { code: 'internal', label: 'Interne / organisation', category: 'Interne', contextType: 'internal', billable: false },
  { code: 'other', label: 'Autre', category: 'Autre', contextType: 'other', billable: false },
];

const fallbackPhases = ['Chiffrage', 'Etudes conception', 'Etudes execution', 'Reunion chantier', 'Reunion synthese', 'DOE'];
const phaseLabels = { chiffrage: 'Chiffrage', conception: 'Etudes conception', execution: 'Etudes execution', doe: 'DOE' };
const managementLabels = { reunionChantier: 'Reunion chantier', reunionSynthese: 'Reunion synthese', pointsClients: 'Entretien client', recetteUsine: 'Recette usine' };
const deliverablePhaseCodes = { 'Etudes conception': 'CON', 'Etudes execution': 'EXE' };

const getTodayDate = () => new Date().toISOString().slice(0, 10);
const isoDate = (date) => date.toISOString().slice(0, 10);
const mondayOf = (value) => {
  const d = new Date(`${value || getTodayDate()}T12:00:00`);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return isoDate(d);
};
const plusDays = (value, days) => {
  const d = new Date(`${value}T12:00:00`);
  d.setDate(d.getDate() + days);
  return isoDate(d);
};
const formatDate = (value) => value ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR') : '-';
const formatHours = (value) => `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;
const getEntryDate = (entry) => String(entry.entryDate || entry.date || '').slice(0, 10);
const getSelectedLabels = (values = {}, labels = {}) => Object.entries(values || {}).filter(([, selected]) => selected === true).map(([key]) => labels[key] || key);
const projectLabel = (project) => project?.name || project?.projectName || project?.project_name || project?.reference || `Projet #${project?.id}`;
const normalizeActivity = (item) => { const contextType = item.contextType || item.context_type || item.context || 'other'; return { ...item, contextType, requiresProject: Boolean(item.requiresProject ?? item.requires_project ?? (contextType === 'project')) }; };

const emptyForm = (projectId = '') => ({
  entryDate: getTodayDate(),
  activityCode: projectId ? 'production' : 'internal',
  projectId: projectId || '',
  phase: projectId ? fallbackPhases[0] : '',
  deliverable: '',
  taskId: '',
  hours: '',
  comment: '',
});

function BadgerPage() {
  const { projectId: routeProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedTaskId = searchParams.get('taskId');
  const projectMode = Boolean(routeProjectId);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState(FALLBACK_ACTIVITIES);
  const [tasks, setTasks] = useState([]);
  const [project, setProject] = useState(null);
  const [ldaRows, setLdaRows] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(() => emptyForm(routeProjectId));
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState(null);
  const [weekStart, setWeekStart] = useState(() => mondayOf(getTodayDate()));
  const [weekSummary, setWeekSummary] = useState(null);
  const [entryFilter, setEntryFilter] = useState('all');

  const selectedActivity = activities.find((item) => item.code === form.activityCode) || activities[activities.length - 1] || FALLBACK_ACTIVITIES[0];
  const selectedProjectId = projectMode ? routeProjectId : form.projectId;
  const needsProject = Boolean(selectedActivity.requiresProject || selectedActivity.contextType === 'project');
  const currentUser = useMemo(() => { try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch { return {}; } }, []);
  const activityGroups = useMemo(() => activities.reduce((groups, activity) => { const category = activity.category || 'Autre'; (groups[category] ||= []).push(activity); return groups; }, {}), [activities]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = projectMode
        ? await getProjectTimeEntries(routeProjectId)
        : await getMyTimeEntries({ from: plusDays(weekStart, -28), to: plusDays(weekStart, 34) });
      setEntries(Array.isArray(data) ? data : []);
      if (!projectMode) setWeekSummary(await getMyWeekSummary(weekStart));
    } catch (error) {
      setStatus({ severity: 'error', message: error.message || 'Impossible de charger les temps saisis.' });
    } finally { setLoading(false); }
  };

  useEffect(() => { loadEntries(); }, [projectMode, routeProjectId, weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getTimeActivities().then((data) => {
      const rows = (Array.isArray(data) ? data : []).map(normalizeActivity);
      if (rows.length) setActivities(rows);
    }).catch(() => setActivities(FALLBACK_ACTIVITIES));
  }, []);

  useEffect(() => {
    if (projectMode) return;
    Promise.all([
      getAllProjects().catch(() => []),
      getInterventions({ pageSize: 100 }).catch(() => ({ items: [] })),
    ]).then(([projectData, interventionData]) => {
      const projectRows = (Array.isArray(projectData) ? projectData : projectData?.projects || projectData?.items || []).map((row) => ({ ...row, realizationType: 'project' }));
      const interventionRows = (Array.isArray(interventionData) ? interventionData : interventionData?.items || []).map((row) => ({ ...row, realizationType: 'intervention' }));
      setProjects([...projectRows, ...interventionRows]);
    }).catch(() => setProjects([]));
  }, [projectMode]);

  useEffect(() => {
    if (!currentUser?.id) return;
    getTimeTrackableTasks(30).then((data) => setTasks(Array.isArray(data) ? data : data?.tasks || data?.data || [])).catch(() => setTasks([]));
  }, [currentUser?.id]);

  useEffect(() => {
    const loadContext = async () => {
      if (!selectedProjectId) { setProject(null); setLdaRows([]); return; }
      try {
        const [projectData, ldaData] = await Promise.all([getOneProject(selectedProjectId), getProjectLdaLines(selectedProjectId)]);
        setProject(projectData || null);
        setLdaRows(Array.isArray(ldaData) ? ldaData : []);
      } catch { setProject(null); setLdaRows([]); }
    };
    loadContext();
  }, [selectedProjectId]);


  useEffect(() => {
    if (!requestedTaskId || !tasks.length || editingId) return;
    const task = tasks.find((item) => String(item.id) === String(requestedTaskId));
    if (!task) return;
    setForm((current) => ({
      ...current,
      activityCode: 'production',
      projectId: task.projectId || task.project_id || current.projectId,
      taskId: String(task.id),
      deliverable: task.ldaLineId || task.lda_line_id || '',
      comment: current.comment || `Tâche : ${task.title || 'Sans titre'}`,
    }));
  }, [requestedTaskId, tasks, editingId]);

  const phaseOptions = useMemo(() => {
    if (!needsProject) return [];
    if (!project) return fallbackPhases;
    const selected = [
      ...getSelectedLabels(project.phases || project.phases_json || {}, phaseLabels),
      ...getSelectedLabels(project.management || project.management_json || {}, managementLabels),
    ];
    return selected.length ? selected : fallbackPhases;
  }, [needsProject, project]);

  useEffect(() => {
    if (!needsProject) {
      setForm((current) => ({ ...current, phase: '', deliverable: '', taskId: '', projectId: projectMode ? routeProjectId : '' }));
      return;
    }
    setForm((current) => ({ ...current, phase: phaseOptions.includes(current.phase) ? current.phase : phaseOptions[0] || '' }));
  }, [needsProject, phaseOptions, projectMode, routeProjectId]);

  const selectedDeliverablePhaseCode = deliverablePhaseCodes[form.phase] || '';
  const deliverableOptions = useMemo(() => !selectedDeliverablePhaseCode ? [] : ldaRows
    .filter((row) => !row.parentId && String(row.phase || row.phase_code || '').toUpperCase() === selectedDeliverablePhaseCode)
    .map((row) => ({ id: row.id, label: row.title || row.denomination || row.codeGed || row.code_ged }))
    .filter((row) => row.id && row.label), [ldaRows, selectedDeliverablePhaseCode]);

  const taskGroups = useMemo(() => {
    const relevant = tasks.filter((task) => !selectedProjectId || String(task.projectId || task.project_id) === String(selectedProjectId));
    const isCompleted = (task) => ['done', 'completed'].includes(String(task.status || '').toLowerCase());
    return {
      active: relevant.filter((task) => !isCompleted(task)),
      recentCompleted: relevant.filter(isCompleted),
    };
  }, [tasks, selectedProjectId]);

  const visibleEntries = useMemo(() => entries
    .filter((entry) => !projectMode || String(entry.projectId) === String(routeProjectId))
    .sort((a, b) => `${getEntryDate(b)}-${b.id}`.localeCompare(`${getEntryDate(a)}-${a.id}`)), [entries, projectMode, routeProjectId]);

  const weekEnd = plusDays(weekStart, 6);
  const weekEntries = useMemo(() => visibleEntries.filter((entry) => getEntryDate(entry) >= weekStart && getEntryDate(entry) <= weekEnd), [visibleEntries, weekEnd, weekStart]);
  const weekTotal = useMemo(() => weekEntries.reduce((sum, entry) => sum + Number(entry.hours || 0), 0), [weekEntries]);
  const todayTotal = useMemo(() => visibleEntries.filter((entry) => getEntryDate(entry) === getTodayDate()).reduce((sum, entry) => sum + Number(entry.hours || 0), 0), [visibleEntries]);
  const productiveTotal = useMemo(() => weekEntries.filter((entry) => entry.contextType === 'project' || entry.context_type === 'project').reduce((sum, entry) => sum + Number(entry.hours || 0), 0), [weekEntries]);
  const dailyTotals = useMemo(() => Array.from({ length: 7 }, (_, index) => {
    const date = plusDays(weekStart, index);
    return { date, total: weekEntries.filter((entry) => getEntryDate(entry) === date).reduce((sum, entry) => sum + Number(entry.hours || 0), 0) };
  }), [weekEntries, weekStart]);
  const gridRows = useMemo(() => {
    const rows = new Map();
    weekEntries.forEach((entry) => {
      const activity = activities.find((item) => item.code === (entry.activityCode || entry.activity_code || (entry.projectId ? 'production' : 'internal')))?.label || 'Autre';
      const projectName = entry.Project ? projectLabel(entry.Project) : '';
      const key = `${entry.contextType || entry.context_type || 'internal'}-${entry.projectId || ''}-${entry.activityCode || entry.activity_code || ''}-${entry.phase || ''}`;
      if (!rows.has(key)) rows.set(key, { key, label: projectName ? `${projectName}${entry.phase ? ` — ${entry.phase}` : ''}` : activity, context: entry, totals: {}, entries: {} });
      const row = rows.get(key); const date = getEntryDate(entry);
      row.totals[date] = (row.totals[date] || 0) + Number(entry.hours || 0);
      (row.entries[date] ||= []).push(entry);
    });
    return [...rows.values()];
  }, [weekEntries, activities]); // eslint-disable-line react-hooks/exhaustive-deps
  const filteredWeekEntries = useMemo(() => weekEntries.filter((entry) => {
    const context = entry.contextType || entry.context_type || 'internal';
    return entryFilter === 'all' || (entryFilter === 'project' ? context === 'project' : context === entryFilter);
  }), [weekEntries, entryFilter]);

  const updateForm = (event) => {
    const { name, value } = event.target;
    if (name === 'taskId') {
      const task = tasks.find((item) => String(item.id) === String(value));
      setForm((current) => ({ ...current, taskId: value, ...(task ? { projectId: task.projectId || task.project_id || current.projectId, deliverable: task.ldaLineId || task.lda_line_id || current.deliverable } : {}) }));
      return;
    }
    setForm((current) => ({ ...current, [name]: value, ...(name === 'phase' ? { deliverable: '' } : {}), ...(name === 'projectId' ? { taskId: '', deliverable: '' } : {}) }));
  };

  const resetForm = () => { setEditingId(null); setForm(emptyForm(routeProjectId)); };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const numericHours = Number.parseFloat(String(form.hours || '').replace(',', '.'));
    if (!form.entryDate || Number.isNaN(numericHours) || numericHours <= 0 || (needsProject && !selectedProjectId)) {
      setStatus({ severity: 'error', message: 'Date, activité, nombre d’heures et projet (pour une activité projet) sont obligatoires.' });
      return;
    }
    const deliverableLabel = deliverableOptions.find((row) => String(row.id) === String(form.deliverable))?.label || '';
    const payload = {
      contextType: selectedActivity.contextType,
      activityCode: selectedActivity.code,
      billable: Boolean(selectedActivity.billable),
      projectId: needsProject ? selectedProjectId : null,
      taskId: needsProject && form.taskId ? form.taskId : null,
      ldaLineId: needsProject && form.deliverable ? form.deliverable : null,
      phase: needsProject ? form.phase : null,
      hours: numericHours,
      date: form.entryDate,
      description: [deliverableLabel ? `Livrable: ${deliverableLabel}` : '', form.comment.trim()].filter(Boolean).join('\n'),
    };
    try {
      if (editingId) await updateTimeEntry(editingId, payload); else await createTimeEntry(payload);
      window.dispatchEvent(new CustomEvent('time-entry-updated', { detail: { projectId: Number(payload.projectId || 0) || null } }));
      resetForm();
      await loadEntries();
      setStatus({ severity: 'success', message: editingId ? 'Saisie modifiée.' : 'Temps enregistré.' });
    } catch (error) { setStatus({ severity: 'error', message: error.message || 'Impossible d’enregistrer le temps.' }); }
  };

  const startEdit = (entry) => {
    const activity = activities.find((item) => item.code === (entry.activityCode || entry.activity_code)) || normalizeActivity(FALLBACK_ACTIVITIES.find((item) => item.code === (entry.projectId ? 'production' : 'internal')) || FALLBACK_ACTIVITIES[0]);
    setEditingId(entry.id);
    setForm({
      entryDate: getEntryDate(entry), activityCode: activity.code, projectId: entry.projectId || '', phase: entry.phase || '',
      deliverable: entry.ldaLineId || entry.lda_line_id || '', taskId: entry.taskId || entry.task_id || '', hours: entry.hours || '', comment: entry.description || entry.comment || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const removeEntry = async (entry) => {
    try {
      await deleteTimeEntry(entry.id);
      window.dispatchEvent(new CustomEvent('time-entry-updated', { detail: { projectId: Number(entry.projectId || 0) || null } }));
      await loadEntries();
      if (editingId === entry.id) resetForm();
      setStatus({ severity: 'info', message: 'Ligne supprimée.' });
    } catch (error) { setStatus({ severity: 'error', message: error.message || 'Impossible de supprimer la ligne.' }); }
  };

  const openCell = (row, date) => {
    const existing = row.entries[date] || [];
    if (existing.length) { startEdit(existing[0]); return; }
    const entry = row.context;
    setEditingId(null);
    setForm({ ...emptyForm(entry.projectId || ''), entryDate: date, activityCode: entry.activityCode || entry.activity_code || (entry.projectId ? 'production' : 'internal'), projectId: entry.projectId || '', phase: entry.phase || '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const copyDay = async (targetDate) => {
    const sourceDate = plusDays(targetDate, -1);
    if (!window.confirm(`Copier les saisies du ${formatDate(sourceDate)} vers le ${formatDate(targetDate)} ?`)) return;
    try { const result = await copyMyTimeDay(sourceDate, targetDate); await loadEntries(); setStatus({ severity: 'success', message: `${result.copiedCount} saisie(s) copiée(s).` }); }
    catch (error) { setStatus({ severity: 'error', message: error.message || 'Impossible de copier la journée.' }); }
  };

  const contextLabel = (entry) => activities.find((item) => item.code === (entry.activityCode || entry.activity_code || (entry.projectId ? 'production' : 'internal')))?.label || 'Autre';

  return (
    <Box className="page-container module-page time-entry-page">
      <div className="time-entry-header">
        <div>
          <Typography variant="h4">Mes temps</Typography>
          <Typography variant="body2" color="textSecondary">
            {projectMode ? `Saisie liée à ${projectLabel(project)}` : 'Mes temps de travail — projets et activités internes'}
          </Typography>
        </div>
        {projectMode ? <Link className="basic-button" to={`/projets/${routeProjectId}`}>Retour au projet</Link> : null}
      </div>

      {status && <Alert severity={status.severity} sx={{ mb: 2 }}>{status.message}</Alert>}

      <div className="time-entry-week-nav">
        <button className="time-entry-text-button" type="button" onClick={() => setWeekStart(mondayOf(getTodayDate()))}>Aujourd’hui</button>
        <button type="button" onClick={() => setWeekStart(plusDays(weekStart, -7))}>‹</button>
        <strong>Semaine du {formatDate(weekStart)} au {formatDate(weekEnd)}</strong>
        <button type="button" onClick={() => setWeekStart(plusDays(weekStart, 7))}>›</button>
      </div>

      <div className="time-entry-summary time-entry-summary--four">
        <div className="time-entry-summary__item"><span>Aujourd'hui</span><strong>{formatHours(todayTotal)}</strong></div>
        <div className="time-entry-summary__item"><span>Cette semaine</span><strong>{formatHours(weekTotal)}</strong></div>
        <div className="time-entry-summary__item"><span>Projet / intervention</span><strong>{formatHours(weekSummary?.projectInterventionTotal ?? productiveTotal)}</strong></div>
        <div className="time-entry-summary__item"><span>Interne</span><strong>{formatHours(weekSummary?.internalTotal ?? (weekTotal - productiveTotal))}</strong></div>
        <div className="time-entry-summary__item"><span>Saisies</span><strong>{weekSummary?.entryCount ?? weekEntries.length}</strong></div>
      </div>

      <div className="time-entry-week-strip">
        {dailyTotals.map(({ date, total }) => <div key={date} className={date === getTodayDate() ? 'is-today' : ''}><span>{new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short' })}</span><strong>{formatHours(total)}</strong><small>{formatDate(date)}</small></div>)}
      </div>

      <Paper className="time-entry-history-panel time-entry-grid-panel">
        <div className="time-entry-panel__header"><Typography variant="h6">Vue hebdomadaire</Typography><small>Cliquez une cellule pour ajouter ou modifier une saisie.</small></div>
        <div className="time-entry-week-grid">
          <div className="time-entry-week-grid__row time-entry-week-grid__header"><span>Activité / contexte</span>{dailyTotals.map(({ date }) => <span key={date}>{new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short' })}</span>)}<span>Total</span></div>
          {gridRows.map((row) => <div className="time-entry-week-grid__row" key={row.key}><strong>{row.label}</strong>{dailyTotals.map(({ date }) => <button type="button" key={date} onClick={() => openCell(row, date)}>{row.totals[date] ? formatHours(row.totals[date]) : '—'}</button>)}<strong>{formatHours(Object.values(row.totals).reduce((sum, hours) => sum + hours, 0))}</strong></div>)}
          <div className="time-entry-week-grid__row time-entry-week-grid__total"><strong>Total</strong>{dailyTotals.map(({ date, total }) => <button type="button" key={date} onClick={() => copyDay(date)} title="Copier les saisies de la journée précédente">{formatHours(total)}</button>)}<strong>{formatHours(weekTotal)}</strong></div>
        </div>
      </Paper>

      <div className="time-entry-layout">
        <Paper className="time-entry-panel">
          <div className="time-entry-panel__header">
            <Typography variant="h6">{editingId ? 'Modifier la saisie' : 'Ajouter du temps'}</Typography>
            {editingId && <button className="time-entry-text-button" type="button" onClick={resetForm}><CloseIcon fontSize="small" /> Annuler</button>}
          </div>
          <form className="time-entry-form" onSubmit={handleSubmit}>
            <Input fullWidth required label="Date" name="entryDate" type="date" value={form.entryDate} onChange={updateForm} />
            <label className="time-entry-field"><span>Activité</span><select name="activityCode" value={form.activityCode} onChange={updateForm}>{Object.entries(activityGroups).map(([category, rows]) => <optgroup label={category} key={category}>{rows.map((a) => <option value={a.code} key={a.code}>{a.label}</option>)}</optgroup>)}</select></label>

            {needsProject && !projectMode && <label className="time-entry-field"><span>Projet</span><select required name="projectId" value={form.projectId} onChange={updateForm}><option value="">Sélectionner un projet</option>{projects.map((p) => <option value={p.id} key={`${p.realizationType || p.realization_type}-${p.id}`}>{(p.realizationType || p.realization_type) === 'intervention' ? 'Intervention — ' : 'Projet — '}{projectLabel(p)}</option>)}</select></label>}
            {needsProject && <label className="time-entry-field"><span>Tâche (facultatif)</span><select name="taskId" value={form.taskId} onChange={updateForm}><option value="">Aucune tâche</option>{taskGroups.active.length > 0 && <optgroup label="Tâches actives">{taskGroups.active.map((task) => <option value={task.id} key={task.id}>{task.title || `Tâche #${task.id}`}</option>)}</optgroup>}{taskGroups.recentCompleted.length > 0 && <optgroup label="Terminées récemment">{taskGroups.recentCompleted.map((task) => <option value={task.id} key={task.id}>{task.title || `Tâche #${task.id}`} — terminée</option>)}</optgroup>}</select><small>Les tâches terminées restent disponibles 30 jours pour permettre un pointage après clôture.</small></label>}
            {needsProject && <label className="time-entry-field"><span>Phase</span><select name="phase" value={form.phase} onChange={updateForm}>{phaseOptions.map((phase) => <option key={phase}>{phase}</option>)}</select></label>}
            {needsProject && selectedDeliverablePhaseCode && <label className="time-entry-field"><span>Livrable (facultatif)</span><select name="deliverable" value={form.deliverable} onChange={updateForm}><option value="">Aucun livrable</option>{deliverableOptions.map((d) => <option value={d.id} key={d.id}>{d.label}</option>)}</select></label>}

            <Input fullWidth required type="number" label="Nombre d'heures" name="hours" value={form.hours} inputProps={{ min: 0, step: 0.25 }} onChange={updateForm} />
            <Input fullWidth label="Commentaire" name="comment" value={form.comment} onChange={updateForm} />
            <button className="basic-button active time-entry-submit" type="submit"><SaveIcon fontSize="small" />{editingId ? 'Enregistrer les modifications' : 'Enregistrer'}</button>
          </form>
        </Paper>

        <Paper className="time-entry-panel time-entry-panel--secondary">
          <div className="time-entry-panel__header"><Typography variant="h6">Répartition de la semaine</Typography></div>
          <div className="time-entry-breakdown">
            {loading ? <CircularProgress size={24} /> : activities.map((activity) => ({ ...activity, hours: weekEntries.filter((entry) => (entry.activityCode || entry.activity_code || (entry.projectId ? 'production' : 'internal')) === activity.code).reduce((sum, entry) => sum + Number(entry.hours || 0), 0) })).filter((item) => item.hours > 0).map((item) => <div className="time-entry-breakdown__row" key={item.code}><span>{item.label}</span><strong>{formatHours(item.hours)}</strong></div>)}
          </div>
        </Paper>
      </div>

      <Paper className="time-entry-history-panel">
        <div className="time-entry-panel__header"><Typography variant="h6">Saisies de la semaine</Typography><div className="time-entry-filters">{[['all', 'Tous'], ['project', 'Projet / intervention'], ['internal', 'Interne'], ['commercial', 'Commercial'], ['marketing', 'Marketing']].map(([value, label]) => <button className={entryFilter === value ? 'is-active' : ''} type="button" key={value} onClick={() => setEntryFilter(value)}>{label}</button>)}</div></div>
        <div className="time-entry-table" role="table" aria-label="Historique des temps">
          <div className="time-entry-table__header" role="row"><span>Date</span><span>Activité</span><span>Contexte</span><span>Phase</span><span>Heures</span><span>Commentaire</span><span>Actions</span></div>
          {loading ? <div className="time-entry-empty"><CircularProgress size={24} /></div> : filteredWeekEntries.length ? filteredWeekEntries.map((entry) => (
            <div className="time-entry-table__row" role="row" key={entry.id}>
              <span>{formatDate(getEntryDate(entry))}</span><span>{contextLabel(entry)}</span><span>{entry.Project ? `${(entry.Project.realizationType || entry.Project.realization_type) === 'intervention' ? 'Intervention' : 'Projet'} — ${projectLabel(entry.Project)}` : entry.projectId ? `Réalisation #${entry.projectId}` : 'Interne'}</span><span>{entry.phase || '—'}</span><strong>{formatHours(entry.hours)}</strong><span>{entry.description || entry.comment || '—'}</span>
              <div className="time-entry-row-actions"><button className="time-entry-icon-button" type="button" onClick={() => startEdit(entry)} title="Modifier"><EditOutlinedIcon fontSize="small" /></button><button className="time-entry-icon-button" type="button" onClick={() => removeEntry(entry)} title="Supprimer"><DeleteIcon fontSize="small" /></button></div>
            </div>
          )) : <div className="time-entry-empty">Aucun temps saisi cette semaine.</div>}
        </div>
      </Paper>
    </Box>
  );
}

export default BadgerPage;
